import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { validPasswordLength } from "@unauthenticated/shared/components/PasswordInput/PasswordValidation/PasswordValidation";
import { EMAIL_REGEX } from "@unauthenticated/shared/context/GlobalConfig/emailRegex";
import { l } from "@unauthenticated/shared/locales/i18n";

import { usePractitionerOauthSignUpSubmission } from "../usePractitionerOauthSignUpSubmission";

interface OidcSignUpFormData {
  firstName: string;
  lastName: string;
  tosTimestamp: Date | "";
  email: string;
  password: string;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  passwordRequired?: boolean;
  tos?: string;
}

interface Args {
  clientId: string;
  onSignUpComplete: () => void;
}

const TOTAL_STEPS = 2;
// Id of the email input on step two; focused when the step advances.
const STEP_TWO_FIRST_INPUT_ID = "oidc-email";

const useOidcSignUpForm = ({ clientId, onSignUpComplete }: Args) => {
  const { t } = useTranslation();

  const [currentSubStep, setCurrentSubStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<OidcSignUpFormData>({
    firstName: "",
    lastName: "",
    tosTimestamp: "",
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [stepAnnouncement, setStepAnnouncement] = useState("");

  const isFirstRender = useRef(true);

  const {
    submit,
    isLoading,
    submitError,
    passwordError,
    setPasswordError,
    isAccountExists,
    setIsAccountExists,
    recaptchaError,
  } = usePractitionerOauthSignUpSubmission({ clientId, onSignUpComplete });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    document.getElementById(STEP_TWO_FIRST_INPUT_ID)?.focus();
    setStepAnnouncement(
      t(l.oauthSignUp.stepAnnouncement, {
        current: currentSubStep,
        total: TOTAL_STEPS,
      })
    );
  }, [currentSubStep, t]);

  const handleFieldChange =
    (field: "firstName" | "lastName" | "email" | "password") =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setFormData(prev => ({ ...prev, [field]: value }));
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
        ...(field === "password" ? { passwordRequired: undefined } : {}),
      }));
      if (field === "password" && passwordError) setPasswordError(null);
    };

  const handleTosChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setFormData(prev => ({ ...prev, tosTimestamp: checked ? new Date() : "" }));
    if (checked) setErrors(prev => ({ ...prev, tos: undefined }));
  };

  const setEmail = (value: string) => {
    setFormData(prev => ({ ...prev, email: value }));
  };

  const isPasswordLengthValid = !!formData.password && validPasswordLength(formData.password);

  const passwordCaption = (() => {
    if (!isPasswordLengthValid) return t(l.common.LengthValidation);

    return t(l.common.LengthConfirmation);
  })();

  const validateStepOne = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!formData.firstName) next.firstName = t(l.oauthSignUp.errors.firstNameRequired);
    if (!formData.lastName) next.lastName = t(l.oauthSignUp.errors.lastNameRequired);
    if (!formData.tosTimestamp) next.tos = t(l.oauthSignUp.errors.tosRequired);

    return next;
  };

  const validateStepTwo = (): FieldErrors => {
    const next: FieldErrors = validateStepOne();
    if (!formData.email) next.email = t(l.oauthSignUp.errors.emailRequired);
    else if (!EMAIL_REGEX.test(formData.email)) next.email = t(l.oauthSignUp.errors.emailInvalid);
    if (!formData.password) next.passwordRequired = true;
    else if (!validPasswordLength(formData.password)) next.password = t(l.common.LengthValidation);

    return next;
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nextErrors = currentSubStep === 1 ? validateStepOne() : validateStepTwo();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    if (currentSubStep === 1) {
      setCurrentSubStep(2);
    } else {
      submit(formData);
    }
  };

  return {
    currentSubStep,
    formData,
    errors,
    isPasswordLengthValid,
    passwordCaption,
    stepAnnouncement,
    isLoading,
    submitError,
    passwordError,
    isAccountExists,
    setIsAccountExists,
    recaptchaError,
    setEmail,
    handleFieldChange,
    handleTosChange,
    handleFormSubmit,
  };
};

export { useOidcSignUpForm };
export type { OidcSignUpFormData };
